import * as React from "react";
import { cn } from "@/lib/utils";
import { useFieldControl } from "./Field";

/** Textareas have no 38px/30px row height to match, so they carry the two type
 *  scales only: md (13.5px) and lg (14px). */
type TextareaSize = "md" | "lg";

type Props = React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
  size?: TextareaSize;
  /** Force the error look. Inside a Field/FormRow with `error` set, this is
   *  already on — pass it only for a standalone control. */
  error?: boolean;
};

/** Design-system textarea. Shares the input's border/fill/radius/focus spec;
 *  resizes vertically by default. */
export const Textarea = React.forwardRef<HTMLTextAreaElement, Props>(
  ({ size = "md", error, className, id, "aria-describedby": describedBy, ...props }, ref) => {
    const f = useFieldControl({ id, describedBy, invalid: error });

    return (
      <textarea
        ref={ref}
        id={f.id}
        aria-describedby={f.describedBy}
        aria-invalid={f.invalid || undefined}
        className={cn(
          "vb-textarea",
          size === "lg" && "vb-textarea--lg",
          f.invalid && "vb-textarea--error",
          className,
        )}
        {...props}
      />
    );
  },
);
Textarea.displayName = "Textarea";

export default Textarea;
